/**
 * Pin-to-bottom scrolling for a streaming transcript — the console's
 * useScrollToBottom, itself ported from capcom's CapGPT chat. Two rules:
 *  1. Follow the output ONLY while the learner is already at (near) the
 *     bottom — a reader scrolling up into history is never yanked down by
 *     an arriving token.
 *  2. Correct the position INSTANTLY and pre-paint (useLayoutEffect) — a
 *     per-chunk 'smooth' animation just queues and restarts, which reads
 *     as stutter.
 */
import { useEffect, useLayoutEffect, useRef, type RefObject } from 'react'

export function useScrollToBottom(
  ref: RefObject<HTMLElement | null>,
  /** Re-evaluated whenever one of these changes (messages, stream text, …). */
  deps: readonly unknown[],
  /** Pixels from the bottom that still count as "following". */
  threshold = 50,
): void {
  const pinnedRef = useRef(true)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    const onScroll = () => {
      pinnedRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < threshold
    }
    el.addEventListener('scroll', onScroll, { passive: true })
    return () => el.removeEventListener('scroll', onScroll)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useLayoutEffect(() => {
    const el = ref.current
    if (el && pinnedRef.current) el.scrollTop = el.scrollHeight
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)
}
